import { ActionButton, Buttons, CancelButton, Drawer, Icon, SingleSelect, Table, Tooltip } from '~/components';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { useDirtyCheck, useForm, useIsMounted } from '~/hooks';
import React, { useEffect, useMemo, useState } from 'react';
import { PageLoader } from '~/routes/public/pages';
import Fuse from 'fuse.js';
import _ from 'lodash';
import { colors } from '~/styles';

export default function ClientMappings({ onClose }) {
  const { workspace } = useWorkspace();
  const api = useApi();
  const toast = useToast();
  const [{ isSubmitting, saved }, form] = useForm();
  const isMounted = useIsMounted();
  const [dirty, setDirty] = useState(false);
  const dirtyCheck = useDirtyCheck(() => dirty);

  const [{ clients, contacts, isReady }, setState] = useState({ clients: null, isReady: false });

  const errorMappings = useMemo(() => {
    if (!clients) return {};
    return clients
      .filter((c) => c.xeroContactId && clients.find((m) => m.xeroContactId === c.xeroContactId && m.id !== c.id))
      .reduce(
        (acc, c) => ({
          ...acc,
          [c.id]: true,
        }),
        {},
      );
  }, [clients]);

  const usedXeroContactIds = useMemo(
    () => clients?.filter((c) => c.xeroContactId).map((m) => m.xeroContactId) || [],
    [clients],
  );

  const hasErrors = useMemo(() => {
    return Object.values(errorMappings).some((error) => error);
  }, [errorMappings]);

  useEffect(() => {
    (async () => {
      const { data: clients } = await api.www.workspaces(workspace.id).xero.getClients();
      const { data: contacts } = await api.www.workspaces(workspace.id).xero.getContacts();

      if (!isMounted.current) return;
      setState({ clients, contacts, isReady: true });
    })();
  }, [api, workspace.id, isMounted]);

  const updateClientMapping = ({ clientId, xeroContactId }) => {
    setDirty(true);

    setState((state) => ({
      ...state,
      clients: clients.map((c) => (c.id === clientId ? { ...c, xeroContactId } : c)),
    }));
  };

  return (
    <Drawer
      isOpen
      title="Client Mappings"
      width="70rem"
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={onClose}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        const handleSave = async () => {
          form.submit();

          try {
            const mappings = clients.map((client) => ({ clientId: client.id, xeroContactId: client.xeroContactId }));
            await api.www.workspaces(workspace.id).xero.updateClientMappings(mappings);
            form.save();
            closeDrawer();
          } catch (error) {
            toast.error(error.message);
            form.done();
          }
        };

        if (!isReady) return <PageLoader />;

        return (
          <>
            <Table style={{ marginBottom: '2rem' }}>
              <Table.BoxHeader>
                <Table.Column width="40%">Client</Table.Column>
                <Table.Column>Xero Contact</Table.Column>
              </Table.BoxHeader>

              <Table.Body>
                {clients.map((client) => {
                  const handleContactChange = (event) => {
                    const xeroContactId = event.target.value || null;

                    updateClientMapping({
                      clientId: client.id,
                      xeroContactId,
                    });
                  };

                  const availableContacts = contacts.filter(
                    (contact) => !usedXeroContactIds.includes(contact.id) || contact.id === client.xeroContactId,
                  );

                  const error = errorMappings[client.id]
                    ? { message: 'Contact already mapped to another client' }
                    : null;

                  return (
                    <Table.BoxRow key={client.id}>
                      <Table.Cell>{client.name}</Table.Cell>
                      <Table.Cell>
                        <ClientSelect
                          clients={availableContacts}
                          value={client.xeroContactId}
                          onChange={handleContactChange}
                          error={error}
                        />
                      </Table.Cell>
                    </Table.BoxRow>
                  );
                })}
              </Table.Body>
            </Table>

            <Drawer.Actions>
              <Buttons align="right">
                <CancelButton onClick={handleCloseClick}>Close</CancelButton>

                <ActionButton isLoading={isSubmitting} ok={saved} onClick={handleSave} disabled={hasErrors}>
                  Save &amp; Close
                </ActionButton>
              </Buttons>
            </Drawer.Actions>
          </>
        );
      }}
    </Drawer>
  );
}

function ClientSelect({ clients, value, error, ...props }) {
  const [filterValue, setFilterValue] = useState('');

  const options = useMemo(() => {
    if (!filterValue) {
      return clients;
    }

    const fuse = new Fuse(clients, { keys: ['name'], threshold: 0.1 });
    const results = fuse.search(filterValue);
    return results.map((result) => result.item);
  }, [clients, filterValue]);

  const selectedOption = useMemo(() => {
    if (!value) return null;
    const client = _.find(clients, { id: value });
    if (!client)
      return {
        id: value,
        name: (
          <Tooltip message="Client not found in Xero">
            <Icon icon="warning" color={colors.warning} spaceRight />
            Client ID: {value}
          </Tooltip>
        ),
      };
    if (error)
      return {
        id: value,
        name: (
          <Tooltip message={error.message}>
            <Icon icon="exclamation-circle" color={colors.error} spaceRight />
            {client.name}
          </Tooltip>
        ),
      };
    return client;
  }, [clients, value, error]);

  return (
    <SingleSelect
      showEmptyOption
      showFilter
      value={value}
      valueRenderer={selectedOption?.name ?? ''}
      onFilterChange={(event) => setFilterValue(event.target.value)}
      {...props}>
      {options.map((option) => (
        // eslint-disable-next-line react/no-unknown-property
        <option key={option.id} value={option.id} tooltip={option.name}>
          {option.name}
        </option>
      ))}
    </SingleSelect>
  );
}
